import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { MEETING } from "@/constants/testIds";
import { ArrowLeft } from "lucide-react";

export default function MeetingRoom() {
  const { id } = useParams();
  const [appt, setAppt] = useState(null);
  const [joined, setJoined] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/appointments/${id}`)
      .then((r) => setAppt(r.data))
      .catch(() => setErr("Meeting not found"));
  }, [id]);

  const room = `pritam-counsel-${id}`;
  const jitsiSrc = `https://meet.jit.si/${room}#config.prejoinPageEnabled=false&config.disableDeepLinking=true&interfaceConfig.MOBILE_APP_PROMO=false`;

  return (
    <div style={{ background: "var(--ink)", minHeight: "100vh", color: "var(--cream)" }}>
      <Navbar dark />
      <main className="pt-28 pb-16 px-6 md:px-12">
        <div className="max-w-[1600px] mx-auto">
          <Link to="/" className="mono-eyebrow inline-flex items-center gap-2" style={{ color: "#a3a3a3" }}>
            <ArrowLeft size={14} /> Back to site
          </Link>

          <div className="mt-8 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-3">
              <div className="mono-eyebrow" style={{ color: "#a3a3a3" }}>§ Meeting Room</div>
              <h1 className="serif text-3xl md:text-4xl mt-3" style={{ letterSpacing: "-0.02em" }}>
                Your session with Mr. Aggarwal
              </h1>
              {appt && (
                <div className="mt-8 space-y-4">
                  <MetaRow k="Topic" v={appt.topic} />
                  <MetaRow k="Date" v={appt.date} />
                  <MetaRow k="Time" v={appt.time_slot} />
                  <MetaRow k="Status" v={appt.status} />
                </div>
              )}
              {err && <div className="mt-6 text-sm" style={{ color: "#e08f77" }}>{err}</div>}
            </div>

            <div className="col-span-12 md:col-span-9" data-testid={MEETING.room}>
              {!joined ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="aspect-[16/9] border flex flex-col items-center justify-center gap-8"
                  style={{ borderColor: "rgba(255,255,255,0.12)", background: "#0f0f10" }}
                >
                  <div className="mono-eyebrow" style={{ color: "#a3a3a3" }}>Waiting to join</div>
                  <h2 className="serif text-4xl md:text-5xl text-center max-w-[520px]" style={{ letterSpacing: "-0.02em" }}>
                    Ready when you are.
                  </h2>
                  <p className="max-w-[420px] text-center text-sm" style={{ color: "#a3a3a3" }}>
                    Camera & microphone will be requested by your browser. Nothing is recorded.
                  </p>
                  <button
                    data-testid={MEETING.joinBtn}
                    onClick={() => setJoined(true)}
                    className="btn-terra"
                  >
                    Join the room
                  </button>
                </motion.div>
              ) : (
                <div className="aspect-[16/9] border" style={{ borderColor: "rgba(255,255,255,0.12)" }}>
                  <iframe
                    title="Meeting"
                    src={jitsiSrc}
                    allow="camera; microphone; fullscreen; display-capture; autoplay"
                    style={{ width: "100%", height: "100%", border: 0 }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function MetaRow({ k, v }) {
  return (
    <div className="flex items-baseline justify-between border-b pb-3" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
      <span className="mono-eyebrow" style={{ color: "#a3a3a3" }}>{k}</span>
      <span className="serif text-lg capitalize">{v}</span>
    </div>
  );
}
